import { z } from "zod";
export const EvaluationUsageSchema = z.object({
    inputTokens: z.number().int().nonnegative().nullable(),
    outputTokens: z.number().int().nonnegative().nullable(),
    totalTokens: z.number().int().nonnegative().nullable(),
    latencyMs: z.number().int().nonnegative().nullable(),
}).strict();
export const emptyEvaluationUsage = {
    inputTokens: null, outputTokens: null, totalTokens: null, latencyMs: null,
};
// Unknown is not zero. A partial run never masquerades as a complete cost.
export function aggregateEvaluationUsage(items) {
    const sum = (key) => items.length === 0 || items.some(x => x[key] === null)
        ? null : items.reduce((n, x) => n + x[key], 0);
    return { inputTokens: sum("inputTokens"), outputTokens: sum("outputTokens"), totalTokens: sum("totalTokens"), latencyMs: sum("latencyMs") };
}
const MeaningSchema = z.object({
    meaning: z.string().trim().min(2).max(300),
    // Literal alternatives, not executable regex. This is a lexical proxy,
    // not proof that a model understands (or negates) a meaning.
    anyOf: z.array(z.string().trim().min(2).max(200)).min(1).max(10),
}).strict();
export const SemanticExpectationSchema = z.object({
    requiredMeanings: z.array(MeaningSchema).max(20),
    forbiddenMeanings: z.array(MeaningSchema).max(20),
    outcomeKey: z.string().regex(/^[a-z][a-z0-9_]{0,63}$/u).nullable(),
    transitionDisposition: z.enum(["stay", "node", "handoff", "terminal"]),
    requiredActions: z.array(z.string().regex(/^[a-z_]+$/u)).max(10),
    forbiddenActions: z.array(z.string().regex(/^[a-z_]+$/u)).max(10),
}).strict();
export const SemanticComparisonStartSchema = z.object({
    baselineRunId: z.string().uuid(), candidateRunId: z.string().uuid(),
    title: z.string().trim().min(2).max(200),
}).strict().refine(x => x.baselineRunId !== x.candidateRunId, "Two distinct runs are required.");
export const SemanticReviewSchema = z.object({
    historicalCaseId: z.string().uuid(),
    verdict: z.enum(["candidate", "baseline", "tie", "reject_both"]),
    note: z.string().trim().min(5).max(2000),
}).strict();
export const SemanticCheckSchema = z.object({ code: z.string(), passed: z.boolean(), detail: z.string() }).strict();
export const SemanticComparisonSchema = z.object({
    id: z.string().uuid(), title: z.string(), createdAt: z.string().datetime(),
    baselineRunId: z.string().uuid(), candidateRunId: z.string().uuid(),
    fixtureSnapshotHash: z.string().regex(/^[a-f0-9]{64}$/u),
    baselineVersionId: z.string().uuid(), candidateVersionId: z.string().uuid(),
    baselineUsage: EvaluationUsageSchema, candidateUsage: EvaluationUsageSchema,
    cases: z.array(z.object({
        historicalCaseId: z.string().uuid(), title: z.string(), inputHash: z.string(),
        expectation: SemanticExpectationSchema,
        baseline: z.object({ reply: z.string(), checks: z.array(SemanticCheckSchema), usage: EvaluationUsageSchema, employeeVersionId: z.string().uuid(), knowledgeVersionIds: z.array(z.string().uuid()), runtimeVersion: z.string().nullable(), stagingRuntimeHash: z.string().nullable(), outcomeKey: z.string().nullable(), transitionDisposition: z.string().nullable(), actions: z.array(z.string()) }).strict(),
        candidate: z.object({ reply: z.string(), checks: z.array(SemanticCheckSchema), usage: EvaluationUsageSchema, employeeVersionId: z.string().uuid(), knowledgeVersionIds: z.array(z.string().uuid()), runtimeVersion: z.string().nullable(), stagingRuntimeHash: z.string().nullable(), outcomeKey: z.string().nullable(), transitionDisposition: z.string().nullable(), actions: z.array(z.string()) }).strict(),
    }).strict()).min(5).max(20),
    reviews: z.array(SemanticReviewSchema.extend({ reviewerId: z.string().uuid(), createdAt: z.string().datetime() })).max(1000),
    gate: z.enum(["blocked", "needs_review", "passed"]),
}).strict();
export const STAGING_TOOL_SNAPSHOT = {
    schemaVersion: "historical_replay_tools_v1",
    schedule: { bindings: [{ bindingKey: "sandbox_consultation", displayName: "Тестовый график", timezone: "Europe/Moscow", slotDurationMinutes: 60,
                availableSlots: [{ localTime: "2030-01-02T11:00", label: "2 января, 11:00 МСК — тест" }, { localTime: "2030-01-03T15:00", label: "3 января, 15:00 МСК — тест" }] }] },
};
export function sandboxSlots(createdAt, timezone) {
    return [11, 15, 18].map((hour, index) => {
        const date = new Date(Date.parse(createdAt) + (index + 1) * 86_400_000).toISOString().slice(0, 10);
        return { localTime: `${date}T${hour}:00`, label: `${date}, ${hour}:00 (${timezone}, тест)` };
    });
}
// Ready means the configured main Yana gateway responds; not semantic acceptance.
export const SemanticStagingTargetSchema = z.object({
    kind: z.enum(["candidate", "current", "unverified"]),
    revision: z.string().regex(/^[a-f0-9]{12,40}$/u).nullable(),
    dirty: z.boolean().nullable(),
}).strict();
export const SemanticStagingStatusSchema = z.object({
    enabled: z.boolean(), // isolated evaluation storage, NOT model readiness
    mode: z.enum(["blocked", "fixed_response_automation", "ready"]),
    effectFree: z.literal(true),
    agentId: z.literal("yana"),
    workspace: z.literal("active_openclaw"),
    target: SemanticStagingTargetSchema,
    blocker: z.enum(["OPENCLAW_EFFECT_FREE_ENTRYPOINT_UNAVAILABLE", "ISOLATED_EVALUATION_STORAGE_REQUIRED"]).nullable(),
}).strict().superRefine((status, ctx) => {
    if ((status.mode === "fixed_response_automation" || status.mode === "ready") && (!status.enabled || status.blocker !== null)
        || status.mode === "blocked" && status.blocker === null) {
        ctx.addIssue({ code: "custom", message: "Inconsistent staging readiness" });
    }
});
// Context data, never authorization. A live adapter rejects it; only the
// explicitly constructed sandbox-ledger adapter accepts synthetic context.
export const SemanticSyntheticBoundarySchema = z.object({
    schemaVersion: z.literal("semantic_synthetic_v1"),
    effectMode: z.literal("simulated_ledger_only"),
    liveData: z.literal(false),
    providerReads: z.literal(false),
    effectCommands: z.literal(false),
    target: SemanticStagingTargetSchema,
}).strict();
//# sourceMappingURL=semantic-staging.js.map